/**
 * Shared Projects Table Component
 * Standardized table for displaying projects across all dashboard roles
 * Features: Sortable columns, column visibility, skeleton loading
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import StatusBadge from '../../../common/StatusBadge';
import TableSkeleton from '../../../common/TableSkeleton';
import { formatCurrency, formatDate, formatPropertyType } from '../../../../utils/formatters';
import {
  formatProjectLocation,
  formatSidAssignment,
  formatSalesTeam,
  formatSalesTeamLines
} from '../../../../utils/projectDataUtils';
import { WORKFLOW_STATUS } from '../../../../utils/constants';

// Status workflow order for sorting (follows project lifecycle)
const STATUS_WORKFLOW_ORDER = [
  WORKFLOW_STATUS.PENDING_PROJECT_NUMBER,
  WORKFLOW_STATUS.ACTIVE_PROJECT,
  WORKFLOW_STATUS.MEASUREMENT_PENDING,
  WORKFLOW_STATUS.MEASUREMENT_IN_PROGRESS,
  WORKFLOW_STATUS.MEASUREMENT_DONE,
  WORKFLOW_STATUS.TWO_D_PENDING,
  WORKFLOW_STATUS.TWO_D_IN_PROGRESS,
  WORKFLOW_STATUS.TWO_D_PENDING_CLIENT_REVIEW,
  WORKFLOW_STATUS.TWO_D_AMENDMENT_REQUIRED,
  WORKFLOW_STATUS.TWO_D_APPROVED,
  WORKFLOW_STATUS.THREE_D_PENDING,
  WORKFLOW_STATUS.THREE_D_IN_PROGRESS,
  WORKFLOW_STATUS.THREE_D_PENDING_CLIENT_REVIEW,
  WORKFLOW_STATUS.THREE_D_AMENDMENT_REQUIRED,
  WORKFLOW_STATUS.THREE_D_APPROVED,
  WORKFLOW_STATUS.DD_PENDING,
  WORKFLOW_STATUS.DD_IN_PROGRESS,
  WORKFLOW_STATUS.DD_SUBMITTED_TO_QUOTATION,
  WORKFLOW_STATUS.QUOTATION_IN_PROGRESS,
  WORKFLOW_STATUS.READY_FOR_SIGNING,
  WORKFLOW_STATUS.SIGNING_SCHEDULED,
  WORKFLOW_STATUS.SIGNING_AMENDMENT_REQUIRED,
  WORKFLOW_STATUS.PROJECT_CLOSED_WON,
  WORKFLOW_STATUS.PROJECT_CLOSED_LOST,
  WORKFLOW_STATUS.PROJECT_CANCELLED
];

// Column definitions
export const COLUMN_DEFINITIONS = [
  { key: 'projectNumber', label: 'Project Number', required: true },
  { key: 'status', label: 'Status', required: true },
  { key: 'packagePrice', label: 'Package Price', required: false },
  { key: 'location', label: 'Project Location', required: false },
  { key: 'propertyType', label: 'Property Type', required: false },
  { key: 'salesTeam', label: 'Sales Team', required: false },
  { key: 'sid', label: 'SID Assigned', required: false },
  { key: 'createdAt', label: 'Created Date', required: false }
];

// Sort icon component
function SortIcon({ field, sortField, sortDirection }) {
  const isActive = sortField === field;
  return (
    <span className="ml-1 inline-flex flex-col">
      <svg
        className={`w-2 h-2 ${isActive && sortDirection === 'asc' ? 'text-blue-600' : 'text-gray-300'}`}
        fill="currentColor"
        viewBox="0 0 8 4"
      >
        <path d="M4 0L8 4H0L4 0Z" />
      </svg>
      <svg
        className={`w-2 h-2 -mt-0.5 ${isActive && sortDirection === 'desc' ? 'text-blue-600' : 'text-gray-300'}`}
        fill="currentColor"
        viewBox="0 0 8 4"
      >
        <path d="M4 4L0 0H8L4 4Z" />
      </svg>
    </span>
  );
}

export default function ProjectsTable({
  projects,
  onRowClick,
  emptyMessage = 'No projects found',
  loading = false,
  visibleColumns = null, // null = show all columns
  // Sorting props - when provided, parent controls sorting
  sortField: externalSortField,
  sortDirection: externalSortDirection,
  onSort: externalOnSort
}) {
  const navigate = useNavigate();

  // Internal sorting state (used when parent doesn't control sorting)
  const [internalSortField, setInternalSortField] = useState('createdAt');
  const [internalSortDirection, setInternalSortDirection] = useState('desc');

  // Use external sort state if provided, otherwise use internal
  const isControlled = externalSortField !== undefined && externalOnSort !== undefined;
  const sortField = isControlled ? externalSortField : internalSortField;
  const sortDirection = isControlled ? externalSortDirection : internalSortDirection;

  // Determine which columns to show
  const columnsToShow = useMemo(() => {
    if (!visibleColumns) {
      return COLUMN_DEFINITIONS.map(col => col.key);
    }
    return COLUMN_DEFINITIONS
      .filter(col => col.required || visibleColumns[col.key])
      .map(col => col.key);
  }, [visibleColumns]);

  // Get sortable value for a project field
  const getSortValue = (project, field) => {
    switch (field) {
      case 'projectNumber':
        return project.projectNumber || '';
      case 'salesTeam':
        return formatSalesTeam(project.salesAgents, project.salesForm);
      case 'location':
        return formatProjectLocation(project.salesForm);
      case 'propertyType':
        return formatPropertyType(project.salesForm?.propertyType);
      case 'packagePrice':
        return project.currentPackagePrice || 0;
      case 'sid':
        return formatSidAssignment(project.teamMembers);
      case 'createdAt':
        return new Date(project.createdAt).getTime();
      case 'status':
        const statusIndex = STATUS_WORKFLOW_ORDER.indexOf(project.workflowStatus);
        return statusIndex === -1 ? 999 : statusIndex; // Unknown statuses go to end
      default:
        return '';
    }
  };

  // Sort projects (skip if parent controls sorting - data is already sorted)
  const sortedProjects = useMemo(() => {
    if (!projects) return [];

    // If parent controls sorting, data is already sorted
    if (isControlled) {
      return projects;
    }

    // Otherwise, sort internally
    const result = [...projects];

    result.sort((a, b) => {
      const aVal = getSortValue(a, sortField);
      const bVal = getSortValue(b, sortField);

      let comparison = 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      } else {
        comparison = String(aVal).localeCompare(String(bVal));
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [projects, sortField, sortDirection, isControlled]);

  // Handle sort click
  const handleSort = (field) => {
    if (isControlled) {
      // Let parent handle sorting
      externalOnSort(field);
    } else {
      // Handle internally
      if (sortField === field) {
        setInternalSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
      } else {
        setInternalSortField(field);
        setInternalSortDirection('asc');
      }
    }
  };

  const handleRowClick = (project) => {
    if (onRowClick) {
      onRowClick(project);
    } else if (project.id) {
      navigate(`/projects/${project.id}`);
    }
  };

  const handleProjectNumberClick = (e, project) => {
    e.stopPropagation();
    if (project.id) {
      navigate(`/projects/${project.id}`);
    }
  };

  // Show skeleton loading
  if (loading) {
    return <TableSkeleton rows={5} columns={columnsToShow.length} />;
  }

  // Sortable header component
  const SortableHeader = ({ field, children, className = '' }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center">
        {children}
        <SortIcon field={field} sortField={sortField} sortDirection={sortDirection} />
      </div>
    </th>
  );

  // Check if column should be visible
  const isColumnVisible = (key) => columnsToShow.includes(key);

  if (!projects || projects.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {isColumnVisible('projectNumber') && (
              <SortableHeader field="projectNumber">Project Number</SortableHeader>
            )}
            {isColumnVisible('status') && (
              <SortableHeader field="status">Status</SortableHeader>
            )}
            {isColumnVisible('packagePrice') && (
              <SortableHeader field="packagePrice">Package Price</SortableHeader>
            )}
            {isColumnVisible('location') && (
              <SortableHeader field="location">Project Location</SortableHeader>
            )}
            {isColumnVisible('propertyType') && (
              <SortableHeader field="propertyType">Property Type</SortableHeader>
            )}
            {isColumnVisible('salesTeam') && (
              <SortableHeader field="salesTeam">Sales Team</SortableHeader>
            )}
            {isColumnVisible('sid') && (
              <SortableHeader field="sid">SID Assigned</SortableHeader>
            )}
            {isColumnVisible('createdAt') && (
              <SortableHeader field="createdAt">Created Date</SortableHeader>
            )}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {sortedProjects.map((project) => (
            <tr
              key={project.id}
              className="hover:bg-gray-50 cursor-pointer"
              onClick={() => handleRowClick(project)}
            >
              {/* Project Number */}
              {isColumnVisible('projectNumber') && (
                <td className="px-4 py-4 whitespace-nowrap">
                  <button
                    onClick={(e) => handleProjectNumberClick(e, project)}
                    className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {project.projectNumber || 'Pending'}
                  </button>
                </td>
              )}

              {/* Status */}
              {isColumnVisible('status') && (
                <td className="px-4 py-4 whitespace-nowrap">
                  <StatusBadge status={project.workflowStatus} size="sm" />
                </td>
              )}

              {/* Package Price */}
              {isColumnVisible('packagePrice') && (
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {formatCurrency(project.currentPackagePrice || 0)}
                  </div>
                </td>
              )}

              {/* Project Location - 3 rows, smaller font */}
              {isColumnVisible('location') && (
                <td className="px-4 py-3">
                  <div
                    className="text-xs text-gray-900 max-w-[180px] leading-tight"
                    title={formatProjectLocation(project.salesForm)}
                  >
                    {project.salesForm ? (
                      <>
                        {project.salesForm.addressLine1 && (
                          <div className="truncate">{project.salesForm.addressLine1}</div>
                        )}
                        {project.salesForm.addressLine2 && (
                          <div className="truncate">{project.salesForm.addressLine2}</div>
                        )}
                        <div className="truncate">
                          {[
                            project.salesForm.city,
                            project.salesForm.state,
                            project.salesForm.postcode
                          ].filter(Boolean).join(', ') || 'N/A'}
                        </div>
                      </>
                    ) : (
                      <div>N/A</div>
                    )}
                  </div>
                </td>
              )}

              {/* Property Type - 2 rows, smaller font */}
              {isColumnVisible('propertyType') && (
                <td className="px-4 py-3">
                  <div className="text-xs text-gray-900 leading-tight">
                    {(() => {
                      const propertyType = formatPropertyType(project.salesForm?.propertyType);
                      if (propertyType === 'N/A') return <div>N/A</div>;
                      const parts = propertyType.split(' - ');
                      if (parts.length === 2) {
                        return (
                          <>
                            <div>{parts[0]}</div>
                            <div>{parts[1]}</div>
                          </>
                        );
                      }
                      return <div>{propertyType}</div>;
                    })()}
                  </div>
                </td>
              )}

              {/* Sales Team (Agent + Leader) - one row per agent */}
              {isColumnVisible('salesTeam') && (
                <td className="px-4 py-3">
                  <div className="text-xs text-gray-900 leading-tight">
                    {(() => {
                      const lines = formatSalesTeamLines(project.salesAgents, project.salesForm);
                      if (lines.length === 0) return <div>N/A</div>;
                      return lines.map((line, idx) => (
                        <div key={idx}>{line}</div>
                      ));
                    })()}
                  </div>
                </td>
              )}

              {/* SID Assigned */}
              {isColumnVisible('sid') && (
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {formatSidAssignment(project.teamMembers)}
                  </div>
                </td>
              )}

              {/* Created Date */}
              {isColumnVisible('createdAt') && (
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">
                    {formatDate(project.createdAt)}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
